import { useMemo, useRef, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useTranslation } from "react-i18next";
import { normalizeAppLang } from "../../utils/appLanguages";
import {
  TIMER_MODES,
  blockKindLabel,
  blockTypeHeadline,
  buildExerciseSyncLookup,
  canSwapPlanningBlockExercises,
  competencyBlockIdOf,
  exerciseLabel,
  hasCatalogExercise,
  isCompetencyBlockSlot,
  exerciseMinutes,
  isPlaceholderBlock,
  isTypeOnlyBlock,
  isPlanningBlockLocked,
  isPlanningExerciseFinalized,
  laneDuration,
  mismatchTeIds,
  sequenceCount,
  timeTicks,
  wallClockMinutes,
  waveDurationMismatches,
  waveKey,
} from "../../utils/planningSubgroups";
import { isPersonalizedTrainingSlot } from "../../utils/personalizedTrainingSlot";
import { localizedCbName } from "../../utils/localizedCatalogFields";
import "./PlanningSubgroupBoard.css";

function blockNumberLabel(n) {
  const value = Number(n);
  if (!Number.isFinite(value) || value < 1) return "";
  return String(value).padStart(2, "0");
}

function BlockTopline({ numberLabel, headline }) {
  if (!numberLabel && !headline) return null;
  return (
    <div className="psg-block__topline">
      {numberLabel ? <span className="psg-block__index">{numberLabel}</span> : null}
      {headline ? <span className="psg-block__kind">{headline}</span> : null}
    </div>
  );
}

function hexToRgb(hex) {
  const h = String(hex || "#0877BC").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  if (Number.isNaN(n)) return "8, 119, 188";
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function ComposeActions({
  tsgId,
  teId,
  compact,
  canEdit,
  highlightActions,
  tourAnchor,
  onSelectLane,
  onAddExercise,
  onApplyTemplate,
  t,
}) {
  if (!canEdit && !highlightActions) return null;
  return (
    <div
      className={`psg-compose${compact ? " psg-compose--compact" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="psg-compose__btn"
        data-tour-id={tourAnchor ? "coach-add-exercise" : undefined}
        aria-label={t("addex") || "Add exercise"}
        onClick={(e) => {
          e.stopPropagation();
          if (!canEdit) return;
          onSelectLane?.(tsgId);
          onAddExercise?.(tsgId, teId);
        }}
      >
        <AddIcon sx={{ fontSize: compact ? 18 : 20 }} />
        <span className="psg-compose__label psg-compose__label--full">
          {t("addex") || "Add exercise"}
        </span>
        <span className="psg-compose__label psg-compose__label--short">
          {t("addexShort") || t("addex") || "Add"}
        </span>
      </button>
      <button
        type="button"
        className="psg-compose__btn psg-compose__btn--ghost"
        aria-label={t("useTemplate") || "Use template"}
        onClick={(e) => {
          e.stopPropagation();
          if (!canEdit) return;
          onSelectLane?.(tsgId);
          onApplyTemplate?.(tsgId, teId);
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: compact ? 18 : 20 }} />
        <span className="psg-compose__label psg-compose__label--full">
          {t("useTemplate") || "Use template"}
        </span>
        <span className="psg-compose__label psg-compose__label--short">
          {t("useTemplateShort") || t("useTemplate") || "Template"}
        </span>
      </button>
    </div>
  );
}

export function PlanningSubgroupBoard({
  lanes = [],
  selectedTsgId,
  selectedTeId,
  sessionDuration,
  canEdit,
  highlightActions = false,
  onSelectLane,
  onSelectExercise,
  onSplit,
  onRename,
  onToggleTimer,
  onDelete,
  onApplyTemplate,
  onAddExercise,
  onOpenDetails,
  onEditExercise,
  onSwapBlocks,
  swapSourceTeId = null,
  onStartSwap,
  onCancelSwap,
  swapBusy = false,
  onSyncBlock = null,
  onDeleteBlock = null,
  lockedTeIds = null,
  /** Optional live map te_id → state id (socket / realtime). */
  teStateById = null,
  /** Optional Pingflo voice control rendered in the board hero. */
  pingfloSlot = null,
  /** Catalog competency blocks for the quick "Change block" menu. */
  competencyBlocks = [],
  onLoadCompetencyBlocks = null,
  onChangeCompetencyBlock = null,
  competencyBlockBusy = false,
}) {
  const { t, i18n } = useTranslation();
  const lng = normalizeAppLang(i18n.language);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cbMenuAnchor, setCbMenuAnchor] = useState(null);
  const [cbMenuLoading, setCbMenuLoading] = useState(false);
  const lastTapRef = useRef({ id: null, at: 0 });
  const doubleOpenGuardRef = useRef(0);
  const showActions = canEdit || highlightActions;
  let addExerciseTourPlaced = false;
  const takeAddExerciseTour = () => {
    if (addExerciseTourPlaced) return false;
    addExerciseTourPlaced = true;
    return true;
  };

  const selectedLane = useMemo(
    () => lanes.find((l) => String(l.tsg_id) === String(selectedTsgId)) || lanes[0] || null,
    [lanes, selectedTsgId]
  );
  const selectedBlock = useMemo(
    () =>
      (selectedLane?.exercises || []).find(
        (ex) => String(ex.te_id) === String(selectedTeId)
      ) || null,
    [selectedLane, selectedTeId]
  );
  const selectedBlockNumber = useMemo(() => {
    if (!selectedBlock) return 0;
    const ix = (selectedLane.exercises || []).findIndex(
      (ex) => String(ex.te_id) === String(selectedBlock.te_id)
    );
    return ix >= 0 ? ix + 1 : 0;
  }, [selectedLane, selectedBlock]);
  const selectedTypeOnly = isTypeOnlyBlock(selectedBlock);
  const stationLabel = selectedLane
    ? selectedLane.is_main
      ? t("mainGroup") || selectedLane.name || "Main group"
      : selectedLane.name
    : "";

  const openFromDoubleTap = (ex, lane) => {
    const now = Date.now();
    if (now - doubleOpenGuardRef.current < 450) return;
    doubleOpenGuardRef.current = now;
    onSelectLane?.(lane?.tsg_id);
    if (canEdit && isTypeOnlyBlock(ex) && onAddExercise) {
      onAddExercise(lane?.tsg_id, ex?.te_id);
      return;
    }
    onOpenDetails?.(ex, lane);
  };

  const openIfDoubleTap = (ex, lane) => {
    const now = Date.now();
    const last = lastTapRef.current;
    const id = ex?.te_id || `lane-${lane?.tsg_id}`;
    if (String(last.id) === String(id) && now - last.at < 420) {
      lastTapRef.current = { id: null, at: 0 };
      openFromDoubleTap(ex, lane);
      return true;
    }
    lastTapRef.current = { id, at: now };
    return false;
  };

  const mismatches = useMemo(() => waveDurationMismatches(lanes), [lanes]);
  const mismatchIds = useMemo(() => mismatchTeIds(mismatches), [mismatches]);
  const exerciseSyncByTeId = useMemo(
    () => buildExerciseSyncLookup(lanes),
    [lanes]
  );
  const clock = wallClockMinutes(lanes);
  const axisMinutes = Math.max(clock, Number(sessionDuration) || 0, 45);
  const ticks = timeTicks(axisMinutes, axisMinutes > 90 ? 15 : 10);
  const trackPx = Math.max(axisMinutes * 7, 280);
  const dragOccurredRef = useRef(false);
  const dragTeIdRef = useRef(null);
  const swapMode = Boolean(swapSourceTeId);
  const swapSource = useMemo(() => {
    if (!swapSourceTeId) return null;
    for (const lane of lanes) {
      const found = (lane.exercises || []).find(
        (ex) => String(ex.te_id) === String(swapSourceTeId)
      );
      if (found) return found;
    }
    return null;
  }, [lanes, swapSourceTeId]);

  const isBlockLocked = (ex) => {
    if (!ex) return true;
    if (lockedTeIds?.has(String(ex.te_id))) return true;
    return isPlanningBlockLocked(ex);
  };

  const canSwapWith = (fromEx, toEx) =>
    canSwapPlanningBlockExercises(fromEx, toEx, {
      fromLocked: isBlockLocked(fromEx),
      toLocked: isBlockLocked(toEx),
    });

  const selectedCanSwap =
    canEdit && selectedBlock && !isBlockLocked(selectedBlock);
  const selectedCanSync =
    canEdit &&
    selectedBlock &&
    !isPlaceholderBlock(selectedBlock) &&
    !isBlockLocked(selectedBlock) &&
    (lanes || []).length > 1 &&
    typeof onSyncBlock === "function";
  const selectedCanDeleteBlock =
    canEdit &&
    selectedBlock &&
    !isBlockLocked(selectedBlock) &&
    typeof onDeleteBlock === "function";
  const selectedCanChangeCb =
    canEdit &&
    selectedBlock &&
    isCompetencyBlockSlot(selectedBlock) &&
    !isPersonalizedTrainingSlot(selectedBlock) &&
    !isBlockLocked(selectedBlock) &&
    typeof onChangeCompetencyBlock === "function";
  const selectedCbId = selectedCanChangeCb
    ? competencyBlockIdOf(selectedBlock)
    : null;

  const openCbMenu = async (anchor) => {
    setCbMenuAnchor(anchor);
    if (competencyBlocks.length > 0 || !onLoadCompetencyBlocks) return;
    setCbMenuLoading(true);
    try {
      await onLoadCompetencyBlocks();
    } finally {
      setCbMenuLoading(false);
    }
  };

  const pickCompetencyBlock = (cbId) => {
    setCbMenuAnchor(null);
    if (!selectedBlock || String(cbId) === String(selectedCbId || "")) return;
    onChangeCompetencyBlock?.(selectedBlock, cbId, selectedLane);
  };

  useEffect(() => {
    setCbMenuAnchor(null);
  }, [selectedTeId]);

  useEffect(() => {
    if (!swapMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancelSwap?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [swapMode, onCancelSwap]);

  const applySwap = (fromEx, toEx) => {
    if (!canEdit || swapBusy) return;
    if (!canSwapWith(fromEx, toEx)) return;
    onSwapBlocks?.(fromEx, toEx);
  };

  const bindBlockSwap = (ex, lane) => {
    const locked = isBlockLocked(ex);
    const empty = isPlaceholderBlock(ex);
    const isSource = String(swapSourceTeId) === String(ex.te_id);
    const isTarget =
      swapMode &&
      !isSource &&
      canSwapWith(swapSource, ex);
    const canDrag = canEdit && !locked && !swapBusy;
    return {
      locked,
      empty,
      isSource,
      isTarget,
      canDrag,
      draggable: canDrag && !swapMode,
      onDragStart: canDrag
        ? (e) => {
            if (swapMode) return;
            dragOccurredRef.current = false;
            dragTeIdRef.current = ex.te_id;
            e.stopPropagation();
            e.dataTransfer.setData("text/plain", String(ex.te_id));
            e.dataTransfer.effectAllowed = "move";
            if (e.dataTransfer.setDragImage && e.currentTarget) {
              e.dataTransfer.setDragImage(e.currentTarget, 24, 24);
            }
          }
        : undefined,
      onDragEnd: () => {
        dragTeIdRef.current = null;
        setTimeout(() => {
          dragOccurredRef.current = false;
        }, 80);
      },
      onDragOver: canEdit
        ? (e) => {
            const fromId = dragTeIdRef.current || e.dataTransfer.getData("text/plain");
            if (!fromId || String(fromId) === String(ex.te_id)) return;
            const fromEx = lanes
              .flatMap((l) => l.exercises || [])
              .find((row) => String(row.te_id) === String(fromId));
            if (!canSwapWith(fromEx, ex)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            dragOccurredRef.current = true;
          }
        : undefined,
      onDrop: canEdit
        ? (e) => {
            e.preventDefault();
            e.stopPropagation();
            const fromId =
              dragTeIdRef.current || e.dataTransfer.getData("text/plain");
            dragTeIdRef.current = null;
            dragOccurredRef.current = true;
            const fromEx = lanes
              .flatMap((l) => l.exercises || [])
              .find((row) => String(row.te_id) === String(fromId));
            applySwap(fromEx, ex);
          }
        : undefined,
      onClick: (e) => {
        e.stopPropagation();
        if (dragOccurredRef.current) {
          dragOccurredRef.current = false;
          return;
        }
        onSelectLane?.(lane.tsg_id);
        if (swapMode) {
          if (isSource) {
            onCancelSwap?.();
            return;
          }
          applySwap(swapSource, ex);
          return;
        }
        if (openIfDoubleTap(ex, lane)) return;
        onSelectExercise?.(ex);
      },
    };
  };

  return (
    <section
      className="psg-board"
      aria-label={t("planningStations") || "Session stations"}
      style={{ "--psg-track": `${trackPx}px` }}
    >
      <header className="psg-board__hero psg-board__hero--toolbar">
        <div className="psg-board__meta">
          <span className="psg-chip">
            {lanes.length} {t("stations") || "stations"}
          </span>
          <span className="psg-chip">
            {clock} {t("min") || "min"} {t("wallClock") || "on court"}
          </span>
          {mismatches.length > 0 ? (
            <span className="psg-chip psg-chip--warn">
              {t("waveDurationMismatch") ||
                "Synced blocks have different lengths"}
            </span>
          ) : null}
        </div>
        <div className="psg-board__hero-actions">
          {pingfloSlot ? (
            <div className="psg-board__pingflo-slot">{pingfloSlot}</div>
          ) : null}
          {showActions ? (
            <button
              type="button"
              className="psg-board__split"
              data-tour-id="coach-split-group"
              onClick={() => {
                if (!canEdit) return;
                onSplit?.();
              }}
            >
              <CallSplitIcon
                className="psg-board__split-icon"
                sx={{ fontSize: 18 }}
              />
              <span className="psg-board__split-label">
                {t("splitGroup") || "Split group"}
              </span>
            </button>
          ) : null}
        </div>
      </header>

      {selectedLane ? (
        <div className="psg-focus" aria-label={t("planningFocus") || "Planning context"}>
          <div className="psg-focus__copy">
            <span className="psg-focus__station">{stationLabel}</span>
            {selectedBlock ? (
              <span className="psg-focus__block">
                {[
                  selectedBlockNumber
                    ? blockNumberLabel(selectedBlockNumber)
                    : "",
                  blockTypeHeadline(selectedBlock, lng) ||
                    (selectedTypeOnly ? blockKindLabel(selectedBlock, lng) : ""),
                  (() => {
                    const name = exerciseLabel(selectedBlock, lng);
                    const typeName = blockTypeHeadline(selectedBlock, lng);
                    return name && name !== typeName ? name : "";
                  })(),
                ]
                  .filter(Boolean)
                  .join(" · ") ||
                  t("trainingBlock") ||
                  "Training block"}
              </span>
            ) : (
              <span className="psg-focus__block psg-focus__block--muted">
                {t("emptyLaneHint") || "Select a training block"}
              </span>
            )}
          </div>
          <div className="psg-focus__actions">
            {selectedCanChangeCb ? (
              <button
                type="button"
                className="psg-focus__btn"
                data-testid="psg-change-cb"
                aria-haspopup="menu"
                aria-expanded={Boolean(cbMenuAnchor)}
                disabled={competencyBlockBusy || swapBusy || swapMode}
                title={
                  t("changeCompetencyBlockHint") ||
                  "Pick another competency block for this training block"
                }
                onClick={(e) => openCbMenu(e.currentTarget)}
              >
                {competencyBlockBusy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CategoryOutlinedIcon sx={{ fontSize: 16 }} />
                )}
                {t("changeCompetencyBlock") || "Change block"}
              </button>
            ) : null}
            {selectedBlock &&
            (!isPlaceholderBlock(selectedBlock) || selectedTypeOnly) ? (
              <button
                type="button"
                className="psg-focus__btn"
                onClick={() => onOpenDetails?.(selectedBlock, selectedLane)}
              >
                {t("viewDetails") || "View"}
              </button>
            ) : null}
            {canEdit && selectedTypeOnly ? (
              <>
                <button
                  type="button"
                  className="psg-focus__btn"
                  onClick={() => onEditExercise?.(selectedBlock, selectedLane)}
                  title={
                    t("editExerciseTypeHint") ||
                    "Change the exercise type or competency block"
                  }
                >
                  {t("editExerciseType") || "Edit type"}
                </button>
                <button
                  type="button"
                  className="psg-focus__btn psg-focus__btn--primary"
                  onClick={() =>
                    onAddExercise?.(selectedLane?.tsg_id, selectedBlock.te_id)
                  }
                >
                  {t("assignExercise") || "Assign exercise"}
                </button>
              </>
            ) : null}
            {canEdit && selectedBlock && !isPlaceholderBlock(selectedBlock) ? (
              <button
                type="button"
                className="psg-focus__btn psg-focus__btn--primary"
                onClick={() => onEditExercise?.(selectedBlock, selectedLane)}
              >
                {t("editExercise") || "Edit"}
              </button>
            ) : null}
            {canEdit && selectedCanSwap && onStartSwap ? (
              <button
                type="button"
                className={`psg-focus__btn${swapMode ? " psg-focus__btn--ghost" : ""}`}
                disabled={swapBusy}
                onClick={() => {
                  if (swapMode) onCancelSwap?.();
                  else onStartSwap?.(selectedBlock);
                }}
              >
                <SwapHorizIcon sx={{ fontSize: 16 }} />
                {swapMode
                  ? t("cancelSwap") || "Cancel swap"
                  : t("swapExercise") || "Swap block"}
              </button>
            ) : null}
            {selectedCanSync ? (
              <button
                type="button"
                className="psg-focus__btn"
                disabled={swapBusy || swapMode}
                onClick={() => onSyncBlock?.(selectedBlock, selectedLane)}
              >
                <SyncAltIcon sx={{ fontSize: 16 }} />
                {t("syncBlockExercise") || "Sync block"}
              </button>
            ) : null}
            {selectedCanDeleteBlock ? (
              <button
                type="button"
                className="psg-focus__btn psg-focus__btn--danger"
                disabled={swapBusy || swapMode}
                onClick={() => onDeleteBlock?.(selectedBlock, selectedLane)}
                aria-label={t("deleteTrainingBlock") || t("delete") || "Delete"}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                {t("delete") || "Delete"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <Menu
        anchorEl={cbMenuAnchor}
        open={Boolean(cbMenuAnchor) && Boolean(selectedCanChangeCb)}
        onClose={() => setCbMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 220 } } }}
      >
        {selectedCanChangeCb && hasCatalogExercise(selectedBlock) ? (
          <MenuItem disabled dense sx={{ whiteSpace: "normal", maxWidth: 280 }}>
            <ListItemText
              secondary={
                t("changeCompetencyBlockClearsExercise") ||
                "The assigned exercise will be removed so you can pick one from the new block."
              }
            />
          </MenuItem>
        ) : null}
        {cbMenuLoading && competencyBlocks.length === 0 ? (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={16} />
            </ListItemIcon>
            <ListItemText primary={t("loading") || "Loading…"} />
          </MenuItem>
        ) : null}
        {!cbMenuLoading && competencyBlocks.length === 0 ? (
          <MenuItem disabled>
            <ListItemText
              primary={t("noCompetencyBlocks") || "No competency blocks available"}
            />
          </MenuItem>
        ) : null}
        {competencyBlocks.map((cb) => {
          const current = String(cb.cb_id) === String(selectedCbId || "");
          return (
            <MenuItem
              key={cb.cb_id}
              selected={current}
              onClick={() => pickCompetencyBlock(cb.cb_id)}
            >
              <ListItemIcon>
                {current ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText primary={localizedCbName(cb, lng) || cb.cb_id} />
            </MenuItem>
          );
        })}
      </Menu>

      {swapMode ? (
        <div className="psg-swap-banner" role="status">
          <SwapHorizIcon sx={{ fontSize: 16 }} />
          <span>
            {t("swapExerciseHint") ||
              "Tap another block or exercise to swap places."}
          </span>
          <button
            type="button"
            className="psg-swap-banner__cancel"
            onClick={() => onCancelSwap?.()}
          >
            {t("cancel") || "Cancel"}
          </button>
        </div>
      ) : null}

      <div className="psg-board__scroll">
        <div className="psg-board__grid">
          <div className="psg-ruler">
            <div className="psg-ruler__label">{t("time") || "Time"}</div>
            <div className="psg-ruler__track">
              {ticks.map((m) => (
                <span
                  key={m}
                  className="psg-ruler__tick"
                  style={{ left: `${(m / axisMinutes) * 100}%` }}
                >
                  {m}'
                </span>
              ))}
            </div>
          </div>

          {lanes.map((lane) => {
            const selected = String(selectedTsgId) === String(lane.tsg_id);
            const mins = laneDuration(lane);
            const tableCount = lane.tables?.length || 0;
            return (
              <article
                key={lane.tsg_id}
                className={`psg-lane${selected ? " psg-lane--selected" : ""}${
                  lane.timer_mode === TIMER_MODES.INDEPENDENT ? " psg-lane--independent" : ""
                }`}
                style={{ "--psg-accent": lane.color || "#0877BC" }}
                onClick={() => onSelectLane?.(lane.tsg_id)}
              >
                <div className="psg-lane__identity">
                  <div className="psg-lane__name-row">
                    {renameId === lane.tsg_id ? (
                      <input
                        className="psg-lane__name-input"
                        value={renameValue}
                        autoFocus
                        aria-label={t("subGroupName") || "Sub-group name"}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => {
                          onRename?.(lane.tsg_id, renameValue);
                          setRenameId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                          if (e.key === "Escape") setRenameId(null);
                        }}
                      />
                    ) : (
                      <h3
                        className="psg-lane__name"
                        onDoubleClick={(e) => {
                          if (!canEdit) return;
                          e.stopPropagation();
                          setRenameId(lane.tsg_id);
                          setRenameValue(lane.name || "");
                        }}
                      >
                        {lane.is_main
                          ? t("mainGroup") || lane.name || "Main group"
                          : lane.name}
                      </h3>
                    )}
                  </div>
                  <button
                    type="button"
                    className="psg-lane__timer"
                    disabled={!canEdit || lane.is_main}
                    title={
                      lane.timer_mode === TIMER_MODES.INDEPENDENT
                        ? t("timerIndependentHint") || "Own start / pause"
                        : t("timerSyncedHint") ||
                          "Shares the main clock and starts on the first training block. You can change this anytime."
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canEdit || lane.is_main) return;
                      onToggleTimer?.(
                        lane.tsg_id,
                        lane.timer_mode === TIMER_MODES.INDEPENDENT
                          ? TIMER_MODES.SYNCED
                          : TIMER_MODES.INDEPENDENT
                      );
                    }}
                  >
                    {lane.timer_mode === TIMER_MODES.INDEPENDENT
                      ? t("ownTimer") || "Own timer"
                      : t("sharedTimer") || "Same timer"}
                  </button>
                  <div className="psg-lane__stats">
                    <span>
                      {mins} {t("min") || "min"}
                    </span>
                    <span>
                      {(lane.exercises || []).length}{" "}
                      {t("trainingBlocks") || "training blocks"}
                    </span>
                    {tableCount > 0 ? (
                      <span>
                        {tableCount} {t("tables") || "tables"}
                      </span>
                    ) : null}
                  </div>
                  {tableCount > 0 ? (
                    <div className="psg-lane__tables" aria-label={t("assignTables")}>
                      {(lane.tables || []).map((tb) => (
                        <span
                          key={tb.tsgt_id || tb.table_id || tb.table_number}
                          className="psg-lane__table"
                        >
                          T{tb.table_number ?? "?"}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {canEdit ? (
                    <div className="psg-lane__actions">
                      {!lane.is_main ? (
                        <button
                          type="button"
                          className="psg-icon-btn psg-icon-btn--danger"
                          aria-label={t("deleteSubGroup") || "Remove sub-group"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(lane);
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="psg-lane__track">
                  {(lane.exercises || []).length === 0 ? (
                    <div className="psg-empty">
                      <span>
                        {t("emptyLaneHint") ||
                          "Give this station a template of training blocks, or add an exercise."}
                      </span>
                      {!swapMode ? (
                      <ComposeActions
                        tsgId={lane.tsg_id}
                        canEdit={canEdit}
                        highlightActions={highlightActions}
                        tourAnchor={takeAddExerciseTour()}
                        onSelectLane={onSelectLane}
                        onAddExercise={onAddExercise}
                        onApplyTemplate={onApplyTemplate}
                        t={t}
                      />
                      ) : null}
                    </div>
                  ) : (
                    <>
                      {(lane.exercises || []).map((ex, blockIndex) => {
                        const minsBlock = Math.max(exerciseMinutes(ex), 1);
                        const width = Math.max(72, minsBlock * 7);
                        const isSel = String(selectedTeId) === String(ex.te_id);
                        const mismatch = mismatchIds.has(String(ex.te_id));
                        const exerciseSync = exerciseSyncByTeId.get(
                          String(ex.te_id)
                        );
                        const swap = bindBlockSwap(ex, lane);
                        const isPersonalized = isPersonalizedTrainingSlot(ex);
                        const typeOnly =
                          !isPersonalized && swap.empty && isTypeOnlyBlock(ex);
                        const empty = swap.empty && !typeOnly && !isPersonalized;
                        const blockNo = blockIndex + 1;
                        const blockNoLabel = blockNumberLabel(blockNo);
                        const typeHeadline = isPersonalized
                          ? t("personalizedExercise", "Personalized exercise")
                          : blockTypeHeadline(ex, lng) ||
                            (empty && !typeOnly
                              ? t("emptyTrainingBlock") || "Empty block"
                              : "");
                        const exerciseName = exerciseLabel(ex, lng);
                        const exerciseNameLine =
                          isPersonalized
                            ? ""
                            : exerciseName && exerciseName !== typeHeadline
                              ? exerciseName
                              : "";
                        const liveStateId =
                          teStateById?.[ex.te_id] ??
                          teStateById?.[String(ex.te_id)] ??
                          null;
                        const isFinalized =
                          !empty && isPlanningExerciseFinalized(ex, liveStateId);
                        const seqs = sequenceCount(ex);
                        const blockStyle = {
                          "--psg-block-w": `${empty ? Math.max(width, 108) : width}px`,
                          "--psg-accent": lane.color || "var(--brand-primary, #0877BC)",
                          ...(exerciseSync
                            ? { "--psg-sync": exerciseSync.color }
                            : {}),
                          boxShadow: empty
                            ? "none"
                            : `0 4px 12px rgba(${hexToRgb(lane.color)}, 0.18)`,
                        };
                        const swapClass = `${swap.isSource ? " psg-block--swap-source" : ""}${
                          swap.isTarget ? " psg-block--swap-target" : ""
                        }${swapMode && !swap.isSource && !swap.isTarget ? " psg-block--swap-dim" : ""}${
                          swap.canDrag ? " psg-block--draggable" : ""
                        }`;
                        const syncClass = `${exerciseSync ? " psg-block--exercise-sync" : ""}${
                          isPersonalized ? " psg-block--personalized" : ""
                        }${isFinalized ? " psg-block--finalized" : ""}`;
                        if (empty && !typeOnly) {
                          return (
                            <div
                              key={ex.te_id}
                              className={`psg-block psg-block--empty${
                                isSel ? " psg-block--selected" : ""
                              }${swapClass}`}
                              style={blockStyle}
                              data-te-id={String(ex.te_id)}
                              draggable={swap.draggable}
                              onDragStart={swap.onDragStart}
                              onDragEnd={swap.onDragEnd}
                              onDragOver={swap.onDragOver}
                              onDrop={swap.onDrop}
                              onClick={swap.onClick}
                              onDoubleClick={(e) => {
                                if (swapMode) return;
                                e.stopPropagation();
                                onSelectLane?.(lane.tsg_id);
                                onOpenDetails?.(ex, lane);
                              }}
                            >
                              <BlockTopline
                                numberLabel={blockNoLabel}
                                headline={typeHeadline}
                              />
                              <span className="psg-block__name">
                                {swap.isTarget
                                  ? t("swapWithThisBlock") || "Drop to swap here"
                                  : t("emptyBlockHint") || "Pick an exercise or template"}
                              </span>
                              <span className="psg-block__meta">
                                <span className="psg-block__mins">
                                  {minsBlock} {t("min") || "min"}
                                </span>
                              </span>
                              {!swapMode ? (
                                <ComposeActions
                                  tsgId={lane.tsg_id}
                                  teId={ex.te_id}
                                  compact
                                  canEdit={canEdit}
                                  highlightActions={highlightActions}
                                  tourAnchor={takeAddExerciseTour()}
                                  onSelectLane={onSelectLane}
                                  onAddExercise={onAddExercise}
                                  onApplyTemplate={onApplyTemplate}
                                  t={t}
                                />
                              ) : null}
                            </div>
                          );
                        }
                        return (
                          <div
                            key={ex.te_id}
                            role="button"
                            tabIndex={0}
                            className={`psg-block${isSel ? " psg-block--selected" : ""}${
                              mismatch ? " psg-block--mismatch" : ""
                            }${typeOnly ? " psg-block--type-only" : ""}${syncClass}${swapClass}`}
                            style={blockStyle}
                            data-te-id={String(ex.te_id)}
                            data-sync-group={
                              exerciseSync != null
                                ? String(exerciseSync.groupIndex)
                                : undefined
                            }
                            draggable={swap.draggable}
                            onDragStart={swap.onDragStart}
                            onDragEnd={swap.onDragEnd}
                            onDragOver={swap.onDragOver}
                            onDrop={swap.onDrop}
                            title={
                              swapMode && swap.isTarget
                                ? t("swapWithThisBlock") || "Tap to swap places"
                                : mismatch
                                  ? t("waveDurationMismatch") ||
                                    "This block does not match the synced station length"
                                  : exerciseSync
                                    ? t("syncedBlockHint") ||
                                      "Same exercise on this block across stations"
                                    : typeOnly && canEdit
                                      ? t("assignTypeExerciseHint", {
                                          defaultValue:
                                            "Double-tap to choose an exercise of this type",
                                        })
                                      : canEdit
                                        ? t("swapExerciseDragHint") ||
                                          "Drag onto another block to swap places"
                                        : undefined
                            }
                            onClick={swap.onClick}
                            onDoubleClick={(e) => {
                              if (swapMode) return;
                              e.stopPropagation();
                              e.preventDefault();
                              openFromDoubleTap(ex, lane);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                swap.onClick(e);
                              }
                            }}
                          >
                            <BlockTopline
                              numberLabel={blockNoLabel}
                              headline={
                                typeHeadline ||
                                `${t("trainingBlock") || "Training block"} ${waveKey(ex)}`
                              }
                            />
                            {typeOnly ? null : exerciseNameLine ? (
                              <span className="psg-block__name">{exerciseNameLine}</span>
                            ) : null}
                            <span className="psg-block__meta">
                              <span className="psg-block__mins">
                                {minsBlock} {t("min") || "min"}
                              </span>
                              {typeOnly ? (
                                <span className="psg-block__later">
                                  {t("assignExerciseLater") || "Assign later"}
                                </span>
                              ) : null}
                              {seqs > 0 ? (
                                <span className="psg-block__seqs">
                                  {t("sequencesCount", {
                                    count: seqs,
                                    defaultValue: "{{count}} sequences",
                                  })}
                                </span>
                              ) : null}
                              {exerciseSync ? (
                                <span
                                  className="psg-block__sync"
                                  style={{
                                    "--psg-sync": exerciseSync.color,
                                  }}
                                >
                                  <SyncAltIcon sx={{ fontSize: 12 }} aria-hidden />
                                  {t("syncedBlockBadge") || "Synced"}
                                </span>
                              ) : null}
                              {isFinalized ? (
                                <span className="psg-block__finalized">
                                  {t("finalized") || t("exerciseStateFinalized") || "Finalized"}
                                </span>
                              ) : null}
                              {swap.isSource ? (
                                <span className="psg-block__seqs">
                                  {t("swapSourceBadge") || "Swapping"}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        );
                      })}
                      {!swapMode ? (
                      <ComposeActions
                        tsgId={lane.tsg_id}
                        canEdit={canEdit}
                        highlightActions={highlightActions}
                        tourAnchor={takeAddExerciseTour()}
                        onSelectLane={onSelectLane}
                        onAddExercise={onAddExercise}
                        onApplyTemplate={onApplyTemplate}
                        t={t}
                      />
                      ) : null}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteSubGroup") || "Remove sub-group"}</DialogTitle>
        <DialogContent>
          {t("deleteSubGroupHint") ||
            "Exercises move back to the main group. Tables are released."}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t("cancel") || "Cancel"}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              onDelete?.(confirmDelete.tsg_id);
              setConfirmDelete(null);
            }}
          >
            {t("delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default PlanningSubgroupBoard;
